import { formatTarget, type ClientTarget } from '@features/clients';
import { useQuery } from '@tanstack/react-query';

import { MAX_JOBS_LISTED } from '../api/bulk-migrations';
import { isActive } from '../model/job-progress';
import { latestFinishedReportOf, refreshLookupLimited } from '../model/navigation';

import {
    bulkKeys,
    isMissing,
    readAllRows,
    retryUnlessClientError,
    useBulkJob,
    useBulkJobs,
} from './use-bulk-migrations';

import type { JobHeader, TenantRow } from '../schemas/bulk-migration';

/**
 * Everything a launch's final report is built from: the launch, the analysis
 * it came from and the newest finished refresh — headers and EVERY row of
 * each, read page by page. A report on the first page only would be an audit
 * that silently leaves clients out.
 *
 * The refresh is found in the module's job history, read here at the widest
 * window the API lists — not the screen's shorter one — and nothing is built
 * until that read has answered: before it, "never refreshed" is a guess.
 */

export interface FinalReportSources {
    readonly dryRunRows: TenantRow[];
    readonly launchRows: TenantRow[];
    readonly refreshRows: TenantRow[];
}

/** An analysis that is gone (expired, another environment) is no rows, not an error. */
async function rowsOrNothing(
    target: ClientTarget,
    moduleId: string,
    reportId: string | null,
): Promise<TenantRow[]> {
    if (reportId === null) return [];
    try {
        return await readAllRows(target, moduleId, reportId);
    } catch (error) {
        if (isMissing(error)) return [];
        throw error;
    }
}

export function useFinalReport(target: ClientTarget, moduleId: string, launchReportId: string) {
    const id = formatTarget(target) ?? 'unaddressable';
    const launch = useBulkJob(target, moduleId, launchReportId);
    const jobs = useBulkJobs(target, moduleId, MAX_JOBS_LISTED);
    const history: readonly JobHeader[] = jobs.data ?? [];
    const dryRunId = launch.data?.fromReportId ?? null;
    const dryRun = useBulkJob(target, moduleId, dryRunId);
    const refresh = latestFinishedReportOf(history, launchReportId) ?? null;
    const dryRunSettled = dryRunId === null || !dryRun.isPending || dryRun.isError;

    const rows = useQuery({
        // The refresh in the key: a newer finished refresh rebuilds the report.
        queryKey: [
            ...bulkKeys.all,
            'final-report',
            id,
            moduleId,
            launchReportId,
            refresh?.reportId ?? null,
            launch.data?.finishedAt ?? null,
        ],
        queryFn: async (): Promise<FinalReportSources> => {
            const [dryRunRows, launchRows, refreshRows] = await Promise.all([
                rowsOrNothing(target, moduleId, dryRunId),
                readAllRows(target, moduleId, launchReportId),
                refresh === null
                    ? Promise.resolve([])
                    : readAllRows(target, moduleId, refresh.reportId),
            ]);
            return { dryRunRows, launchRows, refreshRows };
        },
        enabled:
            formatTarget(target) !== null &&
            launch.data !== undefined &&
            !isActive(launch.data.state) &&
            jobs.isSuccess &&
            dryRunSettled,
        retry: retryUnlessClientError,
        // A finished job's rows do not change; only a new refresh (the key) does.
        staleTime: Infinity,
    });

    return {
        launch,
        jobs,
        dryRun: dryRun.data ?? null,
        refresh,
        refreshLookupLimited:
            launch.data !== undefined &&
            refreshLookupLimited(history, launch.data, MAX_JOBS_LISTED),
        rows,
    };
}
